import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import MarketingHeader from "@/components/layout/MarketingHeader";
import MarketingFooter from "@/components/layout/MarketingFooter";
import { Logo } from "@/components/brand/Logo";
import { api } from "@/lib/api";
import { optimizeImage } from "@/lib/img";
import {
  ArrowUpRight, ArrowRight, Sparkles, Hash, Check, Star,
  MapPin, Calendar as CalIcon, Search, Download,
} from "lucide-react";
import { openVipModal } from "@/lib/vipModal";

// Moody, side-lit warm portraits — editorial high-fashion feel
const HERO_IMG = "https://customer-assets.emergentagent.com/job_her-brand-builders/artifacts/fo0dcgi0_ChatGPT%20Image%20Jun%2015%2C%202026%2C%2011_33_57%20PM.png";
const HERO_IMG_2 = "https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=1600";
const COMMUNITY_IMG = "https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=1200";
const TESTI_IMG_1 = "https://images.pexels.com/photos/27086922/pexels-photo-27086922.jpeg?auto=compress&cs=tinysrgb&w=400";
const TESTI_IMG_2 = "https://images.pexels.com/photos/12396627/pexels-photo-12396627.jpeg?auto=compress&cs=tinysrgb&w=400";
const TESTI_IMG_3 = "https://images.pexels.com/photos/3992656/pexels-photo-3992656.jpeg?auto=compress&cs=tinysrgb&w=400";

const CITIES = ["Phoenix", "Scottsdale", "Gilbert"];
const CATEGORIES = ["Beauty", "Wellness", "Fitness", "Restaurants", "Retail", "Marketing", "Professional Services", "Real Estate", "Events", "Health"];

const CHANNELS = [
  { name: "wins", topic: "Brag a little." },
  { name: "marketing-help", topic: "Stuck on a campaign?" },
  { name: "brand-clinic", topic: "Logo & identity feedback." },
  { name: "local-collabs", topic: "Find a partner this month." },
  { name: "events", topic: "RSVPs & recaps." },
  { name: "phoenix", topic: "Phoenix chapter." },
  { name: "scottsdale", topic: "Scottsdale chapter." },
  { name: "gilbert", topic: "Gilbert chapter." },
];

const COMMUNITY_MESSAGES = [
  { name: "Maya Reyes", role: "Phoenix · Service", text: "Just hit 200 weekly bookings. The new local SEO checklist worked.", time: "9:12 AM" },
  { name: "Tasha Brooks", role: "Scottsdale · Retail", text: "Looking for a Scottsdale florist for a launch dinner — DM me?", time: "9:18 AM" },
  { name: "Devon Park", role: "Phoenix · Studio", text: "Posting the Q2 content calendar template in #brand-clinic.", time: "9:24 AM" },
];

const TESTIMONIALS = [
  { name: "Maya Reyes", biz: "Saguaro & Sage · Phoenix", img: TESTI_IMG_1,
    quote: "Six months in and I'm fully booked. LocalizeHER didn't give me a network. It gave me a market." },
  { name: "Tasha Brooks", biz: "North & Vine · Scottsdale", img: TESTI_IMG_2,
    quote: "It feels like having a marketing team and a board of directors. Both run by women who actually do the work." },
  { name: "Priya Nair", biz: "House of Linen · Gilbert", img: TESTI_IMG_3,
    quote: "I stopped chasing customers. The HER Guide brings them to my door." },
];

const RESOURCES_PREVIEW = [
  { title: "Local Marketing Playbook", cat: "Playbook" },
  { title: "Instagram Templates", cat: "Social" },
  { title: "Q2 Content Calendar", cat: "Content" },
  { title: "AI Prompts for Founders", cat: "AI" },
  { title: "Brand Voice Workbook", cat: "Brand" },
  { title: "Local SEO Checklist", cat: "SEO" },
];

function SectionLabel() { return null; }

function HeroSection({ onJoinFounding }) {
  return (
    <section
      className="relative min-h-[100vh] flex flex-col"
      style={{ background: "var(--lh-espresso)", color: "var(--lh-cream)" }}
      data-testid="hero-section"
    >
      {/* Background photography with warm duotone */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(42,30,22,0.45) 0%, rgba(42,30,22,0.4) 40%, rgba(42,30,22,0.85) 100%), url(${optimizeImage(HERO_IMG, { w: 1800, q: 82, name: "localizeher-hero" })})`,
          backgroundSize: "cover",
          backgroundPosition: "center 35%",
          filter: "saturate(1.0)",
        }}
        aria-hidden="true"
        data-testid="hero-image"
      />

      {/* Side lighting flare */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 50% at 80% 30%, rgba(201,169,131,0.18) 0%, rgba(201,169,131,0) 70%)",
        }}
        aria-hidden="true"
      />

      <div className="relative z-10 flex-1 flex flex-col">
        <div className="max-w-[1400px] mx-auto w-full px-6 md:px-10 lg:px-14 pt-28 lg:pt-36 pb-10 lg:pb-14 flex-1 flex flex-col items-center justify-center text-center">
          <h1
            className="lh-reveal lh-reveal-2 font-display-bold text-[2.6rem] sm:text-[3.6rem] lg:text-[5.4rem] leading-[1] max-w-5xl mx-auto"
            style={{ color: "var(--lh-cream)" }}
            data-testid="hero-headline"
          >
            The era of <em className="italic" style={{ marginRight: "0.18em" }}>invisible</em>women-owned businesses is over.
          </h1>

          <p
            className="lh-reveal lh-reveal-3 mt-6 lg:mt-8 text-lg lg:text-2xl max-w-2xl mx-auto font-medium leading-snug"
            style={{ color: "rgba(239,224,208,0.85)" }}
          >
            Marketing education, growth tools, and a community built to help women-owned businesses become the go-to brands in their cities.
          </p>

          <div className="lh-reveal lh-reveal-4 mt-10 flex flex-wrap gap-3 justify-center">
            <button onClick={onJoinFounding} className="btn-mauve" data-testid="hero-join-founding-button">
              Reserve Your VIP Spot <ArrowUpRight size={16} />
            </button>
            <Link to="/her-guide" className="btn-ghost-light" data-testid="hero-her-guide-button">
              The HER Guide <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        {/* Marquee tape */}
        <div className="py-5 overflow-hidden" style={{ background: "var(--lh-peach)" }}>
          <div className="lh-marquee text-sm md:text-base font-bold" style={{ color: "var(--lh-espresso-noir)", letterSpacing: "0.22em", textTransform: "uppercase" }}>
            {[...Array(2)].flatMap((_, i) =>
              ["No more invisible businesses", "Visibility is leverage", "Built by women who do",
               "Online & Coming to a City Near You Soon!", "More than networking", "The HER Guide"].map((t, j) => (
                <span key={`${i}-${j}`} className="flex items-center gap-4">
                  <span>{t}</span>
                  <span style={{ color: "var(--lh-espresso-noir)", opacity: 0.65 }}>✦</span>
                </span>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function ManifestoSection() {
  return (
    <section className="py-12 lg:py-16 overflow-hidden section-light" data-testid="manifesto-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        {/* LEFT: typographic story */}
        <div className="lg:col-span-7">
          <h2 className="font-display-bold text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tighter max-w-2xl">
            More than networking.<br /><em>Built for growth.</em>
          </h2>

          <p
            className="mt-10 lg:mt-12 max-w-3xl text-lg lg:text-2xl leading-snug font-medium"
            data-testid="manifesto-statement"
          >
            Most women-owned businesses don't need another <em className="italic">networking event,</em> they need{" "}
            <span style={{ color: "var(--lh-cognac)", fontWeight: 700 }}>visibility</span>,{" "}
            <span style={{ color: "var(--lh-cognac)", fontWeight: 700 }}>strategy</span>,{" "}
            <span style={{ color: "var(--lh-cognac)", fontWeight: 700 }}>community</span>,{" "}
            <span style={{ color: "var(--lh-cognac)", fontWeight: 700 }}>business support</span>, and{" "}
            <span style={{ color: "var(--lh-cognac)", fontWeight: 700 }}>customers.</span>
          </p>

          <p className="mt-8 lg:mt-10 max-w-3xl text-lg lg:text-2xl leading-snug font-medium">
            LocalizeHER combines marketing education, business support, local discovery, coaching,
            and community to help women-owned businesses become <em className="italic">impossible to ignore.</em>
          </p>
        </div>

        {/* RIGHT: editorial portrait */}
        <div className="lg:col-span-5 lg:sticky lg:top-28">
          <figure className="relative overflow-hidden rounded-2xl">
            <img
              src={optimizeImage("https://customer-assets.emergentagent.com/job_her-brand-builders/artifacts/xup7li37_ChatGPT%20Image%20Jun%2016%2C%202026%2C%2001_03_39%20AM.png", { w: 900, name: "localizeher-manifesto-portrait" })}
              alt="Wildflower Palm Springs — a women-owned florist"
              className="w-full h-[440px] lg:h-[520px] object-cover"
              loading="eager"
              decoding="async"
              fetchPriority="high"
              data-testid="manifesto-portrait"
            />
            <figcaption
              className="absolute left-6 bottom-6 right-6 p-5 rounded-xl backdrop-blur-md"
              style={{ background: "rgba(31,26,21,0.55)", border: "1px solid rgba(234,216,199,0.18)" }}
            >
              <div
                className="font-semibold uppercase mb-2"
                style={{ color: "var(--lh-peach)", letterSpacing: "0.3em", fontSize: "0.7rem" }}
              >
                Founders we serve
              </div>
              <div className="text-base md:text-lg leading-snug text-[var(--lh-warm-ivory)] font-medium">
                All local female-owned businesses.
              </div>
            </figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const cards = [
    {
      n: "01",
      title: "Build Your Brand",
      body: "Access marketing tools, templates, AI prompts, and expert guidance — engineered for service businesses, retail, and creative founders.",
      span: "md:col-span-7 md:row-span-2",
      img: "https://customer-assets.emergentagent.com/job_her-brand-builders/artifacts/dfzjak2x_ChatGPT%20Image%20Jun%2015%2C%202026%2C%2011_50_49%20PM.png",
    },
    {
      n: "02",
      title: "Grow Your Visibility",
      body: "Get listed in The HER Guide. Show up when customers search for women-owned businesses in your city.",
      span: "md:col-span-5",
    },
    {
      n: "03",
      title: "Join The Community",
      body: "You don't have to navigate entrepreneurship alone. Ambitious founders, real conversations, local events — show up, be seen, build together.",
      span: "md:col-span-5",
    },
  ];
  return (
    <section
      id="how"
      className="py-12 lg:py-16 section-light relative overflow-hidden"
      data-testid="how-it-works-section"
    >
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="text-center max-w-3xl mx-auto mb-14 lg:mb-16">
          <h2 className="font-display-bold text-5xl md:text-7xl tracking-tighter leading-[0.9]">
            Member <em>Privileges.</em>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-7">
          {[
            {
              img: "https://customer-assets.emergentagent.com/job_her-brand-builders/artifacts/44mhwp0z_ChatGPT%20Image%20Jun%2023%2C%202026%2C%2009_04_29%20AM.png",
              kicker: "Marketing",
              title: "Marketing",
              body: "Tactical templates, playbooks, AI prompts, brand strategy, and senior operators on call.",
            },
            {
              img: "https://customer-assets.emergentagent.com/job_her-brand-builders/artifacts/u9j42f72_ChatGPT%20Image%20Jun%2023%2C%202026%2C%2009_22_20%20AM.png",
              kicker: "Visibility",
              title: "Visibility",
              body: "The HER Guide, our directory of vetted women-owned businesses, surfaced where customers search.",
            },
            {
              img: "https://customer-assets.emergentagent.com/job_her-brand-builders/artifacts/4qyn45zi_ChatGPT%20Image%20Jun%2022%2C%202026%2C%2007_55_23%20PM.png",
              kicker: "Community",
              title: "Community",
              body: "A private channel network for wins, brand-clinic feedback, and local collabs.",
            },
            {
              img: "https://customer-assets.emergentagent.com/job_her-brand-builders/artifacts/z4ctk44d_ChatGPT%20Image%20Jun%2023%2C%202026%2C%2009_50_00%20AM.png",
              kicker: "Coaching",
              title: "Coaching",
              body: "Fractional CMO advisory, business strategy, and brand audits at member rates.",
            },
          ].map((c, i) => (
            <article
              key={c.title}
              className="group rounded-2xl overflow-hidden bg-white transition-all duration-500 hover:-translate-y-1"
              style={{
                border: "1px solid var(--lh-border)",
                boxShadow:
                  "0 1px 0 rgba(31,26,21,0.02), 0 18px 36px -28px rgba(31,26,21,0.18)",
              }}
              data-testid={`privilege-card-${i + 1}`}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={optimizeImage(c.img, { w: 800, name: `localizeher-member-${c.kicker || c.title || "privilege"}` })}
                  alt={c.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                />
              </div>
              <div className="p-7 lg:p-8">
                <div
                  className="font-display-bold mb-4 flex items-baseline gap-3 tracking-tighter leading-[1.0]"
                  style={{ color: "var(--lh-espresso-noir)" }}
                >
                  <span
                    className="font-mono"
                    style={{ color: "var(--lh-cognac)", fontSize: "0.95rem", letterSpacing: "0.05em" }}
                  >
                    0{i + 1}
                  </span>
                  <span className="text-3xl">{c.title}</span>
                </div>
                <p className="text-base font-medium leading-relaxed">
                  {c.body}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function MembershipSection({ onSelect }) {
  const tiers = [
    {
      key: "activate",
      name: "Activate",
      price: "$29",
      tagline: "For women building their foundation.",
      bullets: ["Community access", "Member channels", "Monthly workshops", "Resource library", "Business profile listing", "Local events", "Marketing templates", "AI prompts"],
      dark: false,
      cta: "Reserve Your VIP Spot",
    },
    {
      key: "accelerate",
      name: "Accelerate",
      price: "$89",
      tagline: "For women ready to scale.",
      bullets: ["Everything in Activate", "Strategy sessions", "Business hot seats", "Accountability groups", "Marketing audits", "Priority support", "Featured directory placement", "Coaching discounts"],
      dark: true,
      cta: "Reserve Your VIP Spot",
    },
  ];
  return (
    <section id="membership" className="py-12 lg:py-16 section-light" data-testid="membership-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <SectionLabel />
          <h2 className="font-display-bold text-5xl sm:text-7xl md:text-8xl tracking-tighter leading-[0.9]">
            Pick your <em>orbit.</em>
          </h2>
          <p className="mt-6 text-base sm:text-lg lg:text-2xl leading-snug font-medium">
            Two tiers. Launching this <em>Summer.</em> Reserve your VIP spot and we&rsquo;ll let you know the moment memberships go live.
          </p>
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          {tiers.map((t) => (
            <div
              key={t.key}
              className="tier-card p-6 sm:p-10 lg:p-14 border-2 transition-all duration-500 hover:-translate-y-1 rounded-2xl relative overflow-hidden"
              style={{
                background: "var(--lh-mocha)",
                borderColor: t.dark ? "var(--lh-peach)" : "rgba(234,216,199,0.18)",
              }}
              data-testid={`tier-card-${t.key}`}
            >
              {/* Coming Soon diagonal ribbon — top-left corner */}
              <div
                aria-hidden="true"
                className="absolute top-0 left-0 w-24 h-24 sm:w-32 sm:h-32 pointer-events-none"
                style={{ overflow: "hidden" }}
              >
                <div
                  className="absolute font-bold uppercase text-center"
                  style={{
                    background: "var(--lh-peach)",
                    color: "var(--lh-espresso-noir)",
                    letterSpacing: "0.2em",
                    fontSize: "0.6rem",
                    width: 180,
                    padding: "6px 0",
                    top: 24,
                    left: -54,
                    transform: "rotate(-45deg)",
                    transformOrigin: "center",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                  }}
                  data-testid={`tier-coming-soon-${t.key}`}
                >
                  Coming Soon
                </div>
              </div>
              {t.dark && (
                <div
                  className="absolute top-4 right-4 sm:top-6 sm:right-6 inline-flex items-center px-2.5 py-1 rounded-full font-semibold uppercase"
                  style={{
                    background: "var(--lh-peach)",
                    color: "var(--lh-espresso-noir)",
                    letterSpacing: "0.2em",
                    fontSize: "0.6rem",
                  }}
                >
                  Most popular
                </div>
              )}
              <div className="flex flex-wrap items-end justify-between gap-x-3 gap-y-1 mb-2 pt-10 sm:pt-0">
                <h3 className="font-display-bold text-5xl sm:text-6xl md:text-7xl tracking-tighter text-[var(--lh-warm-ivory)]">{t.name}</h3>
                <div className="text-right">
                  <span className="font-display-bold text-5xl sm:text-6xl text-[var(--lh-warm-ivory)]">{t.price}</span>
                  <span className="text-sm font-semibold" style={{ color: "rgba(234,216,199,0.6)" }}> /mo</span>
                </div>
              </div>
              <p className="text-base font-medium mb-8" style={{ color: "rgba(234,216,199,0.78)" }}>{t.tagline}</p>
              <ul className="space-y-3 mb-10">
                {t.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-base font-medium text-[var(--lh-warm-ivory)]">
                    <Check size={17} style={{ color: "var(--lh-peach)" }} className="mt-1 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => onSelect(t.key)}
                className="btn-ghost-light w-full justify-center"
                data-testid={`tier-cta-${t.key}`}
              >
                {t.cta} <ArrowUpRight size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HerGuideSection() {
  const [city, setCity] = useState("");
  const [category, setCategory] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    setLoading(true);
    const params = {};
    if (city) params.city = city;
    if (category) params.category = category;
    // Silent fetch with one retry — public read-only endpoint, no toast on
    // transient network blips (common on mobile). If it fails twice, we
    // simply leave the section empty rather than throwing an alert.
    const attempt = async () => {
      const { data } = await api.get("/businesses", { params });
      return data;
    };
    try {
      const data = await attempt();
      setResults(data);
    } catch {
      try {
        await new Promise((r) => setTimeout(r, 600));
        const data = await attempt();
        setResults(data);
      } catch {
        // Silent — directory section just stays empty.
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { search(); /* initial */ // eslint-disable-next-line
  }, []);

  return (
    <section id="her-guide" className="py-12 lg:py-16 section-light" data-testid="her-guide-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="grid lg:grid-cols-12 gap-12 items-end mb-12">
          <div className="lg:col-span-7">
            <SectionLabel />
            <h2 className="font-display-bold text-6xl md:text-8xl tracking-tighter leading-[0.9] text-[var(--lh-warm-ivory)]">
              Support local.<br /><em>Support her.</em>
            </h2>
          </div>
          <p className="lg:col-span-5 text-base sm:text-lg lg:text-2xl leading-snug text-[var(--lh-text)] font-medium">
            Discover women-owned businesses in your community — the modern directory built for the
            way locals actually search and shop.
          </p>
        </div>

        {/* Search bar */}
        <div className="border border-[var(--lh-border)] p-6 lg:p-8 mb-10 grid md:grid-cols-12 gap-6 items-end rounded-2xl" style={{ background: "var(--lh-espresso-noir)" }} data-testid="her-guide-search">
          <div className="md:col-span-5">
            <div className="overline text-[var(--lh-muted)] mb-2">Select City</div>
            <select
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="input-line"
              data-testid="her-guide-city-select"
            >
              <option value="">All cities</option>
              {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="md:col-span-5">
            <div className="overline text-[var(--lh-muted)] mb-2">Select Category</div>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="input-line"
              data-testid="her-guide-category-select"
            >
              <option value="">All categories</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <button onClick={search} disabled={loading} className="btn-primary w-full justify-center" data-testid="her-guide-search-button">
              {loading ? "…" : "Find"} <Search size={14} />
            </button>
          </div>
        </div>

        {/* Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="her-guide-results">
          {results.slice(0, 6).map((b) => (
            <article key={b.business_id} className="editorial-card overflow-hidden" data-testid={`business-card-${b.business_id}`}>
              <div className="aspect-[4/3] overflow-hidden bg-[var(--lh-surface)]">
                {b.photo_url && (
                  <img src={optimizeImage(b.photo_url, { w: 800, name: `localizeher-${b.name || "business"}` })} alt={b.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                )}
              </div>
              <div className="p-6">
                <div className="overline text-[var(--lh-mauve)] mb-2">{b.category}</div>
                <h3 className="font-serif-display text-2xl text-[var(--lh-warm-ivory)]">{b.name}</h3>
                <div className="text-xs text-[var(--lh-muted)] mt-1 flex items-center gap-1.5"><MapPin size={12} />{b.city}</div>
                <p className="text-sm text-[var(--lh-text)] font-light mt-3 line-clamp-3">{b.description}</p>
                <div className="mt-5 flex items-center justify-between">
                  <a href={b.website || "#"} target="_blank" rel="noreferrer" className="overline text-[var(--lh-warm-ivory)] hover:text-[var(--lh-mauve)]">Visit →</a>
                  {b.instagram && <span className="overline text-[var(--lh-muted)]">@{b.instagram}</span>}
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/her-guide" className="btn-ghost" data-testid="her-guide-explore-all">
            Explore the full directory <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function CommunitySection() {
  return (
    <section id="community" className="py-12 lg:py-16 section-light overflow-hidden" data-testid="community-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <SectionLabel />
          <h2 className="font-display-bold text-6xl md:text-8xl tracking-tighter leading-[0.9]">
            You're not<br /><em>building alone.</em>
          </h2>
          <p className="mt-6 text-lg lg:text-2xl leading-snug font-medium max-w-md">
            A private community with channels for wins, marketing help, brand clinics, and
            city chapters. Real founders. Real answers. No fluff.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {CHANNELS.slice(0, 6).map((c) => (
              <span
                key={c.name}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-full"
                style={{ background: "rgba(122,88,66,0.12)", color: "var(--lh-caramel)" }}
              >
                <Hash size={12} /> {c.name}
              </span>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7 relative min-h-[560px]" data-testid="community-preview">
          {COMMUNITY_MESSAGES.map((m, i) => {
            const positions = [
              { top: "0%",   left: "0%",  width: "78%", rotate: "-1.2deg" },
              { top: "32%",  left: "22%", width: "78%", rotate: "0.8deg" },
              { top: "64%",  left: "4%",  width: "82%", rotate: "-0.6deg" },
            ];
            const p = positions[i] || positions[0];
            return (
              <div
                key={m.name}
                className="absolute rounded-2xl shadow-[0_30px_60px_-30px_rgba(31,26,21,0.35)]"
                style={{
                  background: "#FFFFFF",
                  border: "1px solid rgba(200,191,180,0.5)",
                  padding: "1.25rem 1.5rem",
                  top: p.top,
                  left: p.left,
                  width: p.width,
                  transform: `rotate(${p.rotate})`,
                  color: "var(--lh-espresso-noir)",
                }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 font-display-bold"
                    style={{ background: "var(--lh-peach)", color: "var(--lh-espresso-noir)" }}
                  >
                    {m.name[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-sm text-[var(--lh-espresso-noir)]">{m.name}</span>
                      <span className="text-xs text-[var(--lh-greige)] font-medium">{m.role}</span>
                      <span className="text-xs text-[var(--lh-greige)] ml-auto">{m.time}</span>
                    </div>
                    <p className="text-sm leading-relaxed text-[var(--lh-espresso-noir)] font-medium">{m.text}</p>
                    <div className="mt-3 flex items-center gap-3 text-xs text-[var(--lh-greige)]">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ background: "rgba(255,168,115,0.18)", color: "var(--lh-caramel)" }}>
                        <Sparkles size={10} /> {8 + i * 3} replies
                      </span>
                      <span>· last reply 1d ago</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function EventsSection({ events }) {
  return (
    <section id="events" className="py-12 lg:py-16 bg-[var(--lh-surface)]" data-testid="events-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-10 gap-6">
          <div>
            <SectionLabel />
            <h2 className="font-display-bold text-6xl md:text-8xl tracking-tighter leading-[0.9] text-[var(--lh-warm-ivory)]">
              Meet the women<br /><em>changing your city.</em>
            </h2>
          </div>
          <button
            type="button"
            onClick={() => openVipModal("interested", "events-see-all")}
            className="btn-ghost-light"
            data-testid="events-rsvp-link"
          >
            See all events <ArrowRight size={16} />
          </button>
        </div>

        {/* Map module — full US map with current cities + virtual badge */}
        <div
          className="relative rounded-2xl p-6 lg:p-8 mb-14 overflow-hidden"
          style={{ background: "var(--lh-warm-linen)", border: "1px solid rgba(200,191,180,0.4)" }}
          data-testid="events-map"
        >
          <div className="absolute top-6 right-6 z-10 flex flex-col items-end gap-3">
            <span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold uppercase shadow-md"
              style={{
                background: "var(--lh-peach)",
                color: "var(--lh-espresso-noir)",
                letterSpacing: "0.22em",
                fontSize: "0.7rem",
              }}
            >
              ● Virtual · Online events
            </span>
            <span className="text-xs font-medium max-w-[220px] text-right" style={{ color: "var(--lh-espresso-noir)" }}>
              Open to members everywhere. Coming to a city near you soon.
            </span>
          </div>
          <img
            src={optimizeImage("https://customer-assets.emergentagent.com/job_her-brand-builders/artifacts/8mhfmobh_ChatGPT%20Image%20Jun%2016%2C%202026%2C%2011_52_08%20AM.png", { w: 1400, name: "localizeher-events-map" })}
            alt="LocalizeHER in-person events map of the United States"
            className="w-full h-auto object-contain"
            loading="lazy"
            decoding="async"
            data-testid="events-map-image"
          />
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {events.slice(0, 4).map((e) => {
            const d = new Date(e.date);
            const day = d.getDate();
            const month = d.toLocaleString("en", { month: "short" }).toUpperCase();
            return (
              <article key={e.event_id} className="editorial-card p-7 flex flex-col" data-testid={`event-card-${e.event_id}`}>
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="font-serif-display text-5xl leading-none text-[var(--lh-warm-ivory)]">{day}</div>
                    <div className="overline text-[var(--lh-peach)] mt-1">{month}</div>
                  </div>
                  <span className="text-[0.7rem] font-bold text-[var(--lh-muted)] px-3 py-1.5 rounded-full" style={{ background: "var(--lh-cream-warm)", letterSpacing: "0.18em", textTransform: "uppercase" }}>{e.city}</span>
                </div>
                <h3 className="font-serif-display text-xl text-[var(--lh-warm-ivory)] mb-2 leading-tight">{e.title}</h3>
                <p className="text-xs text-[var(--lh-muted)] flex items-center gap-1 mb-1"><CalIcon size={11} /> {e.time}</p>
                <p className="text-xs text-[var(--lh-muted)] flex items-center gap-1"><MapPin size={11} /> {e.venue}</p>
                <p className="text-sm font-light text-[var(--lh-text)] mt-4 flex-1 line-clamp-3">{e.description}</p>
                <button
                  type="button"
                  onClick={() => openVipModal("interested", `event-rsvp:${e.event_id}`)}
                  className="overline text-[var(--lh-warm-ivory)] hover:text-[var(--lh-peach)] mt-5 inline-flex items-center gap-2"
                >
                  RSVP <ArrowRight size={12} />
                </button>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CoachingResourcesSection() {
  return (
    <section
      id="resources"
      className="py-12 lg:py-16 section-light"
      data-testid="fractional-cmo-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Photo */}
        <div className="lg:col-span-6 order-2 lg:order-1">
          <div className="rounded-2xl overflow-hidden">
            <img
              src={optimizeImage("https://customer-assets.emergentagent.com/job_her-brand-builders/artifacts/0jn4kw8z_ChatGPT%20Image%20Jun%2023%2C%202026%2C%2010_29_52%20AM.png", { w: 900 })}
              alt="A founder on a fractional CMO advisory session"
              className="w-full h-[420px] lg:h-[560px] object-cover"
              loading="lazy"
              decoding="async"
              data-testid="fractional-cmo-photo"
            />
          </div>
        </div>

        {/* Copy */}
        <div className="lg:col-span-6 order-1 lg:order-2">
          <div className="overline mb-4" style={{ color: "var(--lh-cognac)" }}>
            Coaching · Members Only
          </div>
          <h2 className="font-display-bold text-5xl md:text-7xl tracking-tighter leading-[0.92]">
            Need expert eyes<br /><em>on your business?</em>
          </h2>
          <p className="mt-7 text-lg lg:text-xl font-medium leading-relaxed max-w-xl">
            Senior marketing leadership without the senior price tag. A Fractional CMO
            partners with you on strategy, channel mix, and quarterly priorities — at
            LocalizeHER member rates.
          </p>
          <ul className="mt-7 space-y-2.5 text-base font-medium max-w-xl">
            {[
              "Quarterly marketing strategy + roadmap",
              "Channel allocation, budget, and KPI review",
              "Brand audit and positioning sharpening",
              "Senior operator on-call for the big decisions",
            ].map((b) => (
              <li key={b} className="flex items-baseline gap-3">
                <span style={{ color: "var(--lh-peach)" }}>✦</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <a
            href="https://calendly.com/localizeher/fractional-cmo-discovery-call"
            target="_blank"
            rel="noreferrer"
            className="btn-primary mt-10"
            data-testid="fractional-cmo-cta"
          >
            Hire a Fractional CMO <ArrowUpRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  const [i, setI] = useState(0);
  const t = TESTIMONIALS[i];
  return (
    <section className="py-12 lg:py-16 bg-[var(--lh-surface)]" data-testid="testimonials-section">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-14 text-center">
        <SectionLabel />
        <blockquote className="font-display-bold text-4xl md:text-6xl leading-[1.05] tracking-tight text-[var(--lh-warm-ivory)] max-w-4xl mx-auto" data-testid="testimonial-quote">
          “{t.quote}”
        </blockquote>
        <div className="mt-12 flex items-center justify-center gap-4">
          <img src={optimizeImage(t.img, { w: 200, name: `localizeher-member-${t.name || "avatar"}` })} alt={t.name} loading="lazy" decoding="async" className="w-14 h-14 object-cover" />
          <div className="text-left">
            <div className="font-medium">{t.name}</div>
            <div className="overline text-[var(--lh-muted)]">{t.biz}</div>
          </div>
        </div>
        <div className="mt-8 flex items-center justify-center gap-3">
          {TESTIMONIALS.map((t, idx) => (
            <button
              key={t.name}
              onClick={() => setI(idx)}
              aria-label={`Show testimonial ${idx + 1}`}
              data-testid={`testimonial-dot-${idx}`}
              className={`w-2 h-2 rounded-full transition-all ${i === idx ? "bg-[var(--lh-mauve)] scale-125" : "bg-[var(--lh-border)]"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCtaSection({ onJoinFounding }) {
  return (
    <section
      className="py-14 lg:py-20 relative overflow-hidden"
      style={{ background: "var(--lh-soft-blush)" }}
      data-testid="final-cta-section"
    >
      {/* Subtle peach radial accent */}
      <div
        className="absolute inset-0 pointer-events-none opacity-60"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, rgba(255,168,115,0.20) 0%, transparent 45%), radial-gradient(circle at 85% 70%, rgba(166,119,92,0.12) 0%, transparent 50%)",
        }}
        aria-hidden="true"
      />
      <div className="relative max-w-[1100px] mx-auto px-6 md:px-10 lg:px-14 text-center">
        <h2 className="font-display-bold text-6xl md:text-8xl tracking-tighter leading-[0.9]" style={{ color: "var(--lh-espresso-noir)" }}>
          Ready to become<br />the name <em>everyone knows?</em>
        </h2>
        <p className="mt-8 font-medium text-lg max-w-2xl mx-auto" style={{ color: "var(--lh-espresso-noir)", opacity: 0.78 }}>
          Lock in a Founding Member Rate and start building the brand your business deserves.
        </p>
        <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
          <button onClick={onJoinFounding} className="btn-primary" data-testid="final-join-founding-button">
            Reserve Your VIP Spot <ArrowUpRight size={16} />
          </button>
          <Link
            to="/her-guide"
            className="btn-ghost"
            style={{
              color: "var(--lh-espresso-noir)",
              borderColor: "var(--lh-espresso-noir)",
            }}
            data-testid="final-her-guide-button"
          >
            Explore The HER Guide <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

import FoundingLeadModal from "@/components/marketing/FoundingLeadModal";

export default function Landing() {
  const [events, setEvents] = useState([]);
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadTier, setLeadTier] = useState("interested");
  const [leadSource, setLeadSource] = useState("landing");

  useEffect(() => {
    api.get("/events").then(({ data }) => setEvents(data)).catch(() => setEvents([]));
  }, []);

  const openLead = (tier = "interested", source = "landing") => {
    setLeadTier(tier);
    setLeadSource(source);
    setLeadOpen(true);
  };

  const handleJoinFounding = () => openLead("interested", "landing-cta");
  const handleSelectTier = (tier) => openLead(tier, "tier-card");

  return (
    <div data-testid="landing-page">
      <MarketingHeader />
      <HeroSection onJoinFounding={handleJoinFounding} />
      <ManifestoSection />
      <HowItWorksSection />
      <MembershipSection onSelect={handleSelectTier} />
      <HerGuideSection />
      <CommunitySection />
      <EventsSection events={events} />
      <CoachingResourcesSection />
      <TestimonialsSection />
      <FinalCtaSection onJoinFounding={handleJoinFounding} />
      <MarketingFooter />
      <FoundingLeadModal
        open={leadOpen}
        onClose={() => setLeadOpen(false)}
        tier={leadTier}
        source={leadSource}
      />
    </div>
  );
}
